// Which lifecycle actions an admin sees on a test, who may approve a waiting
// economy action, and how an action's answer is worded. The server is the
// authority on every rule; this only keeps the page from offering what it would
// refuse, and passes its refusals through word for word.
import type {
  ExpV2Action,
  ExpV2ActionResult,
  ExpV2PendingAction,
  ExpV2Test,
} from '@/types/experimentsV2';
import { apiErrorMessage } from '@/utils/apiError';
import { CONTROL } from './draftForm';
import { formatPercent, percentToBuckets } from './units';

/** On an economy test these wait for a second admin; pause, remove and end apply at once. */
export const ECONOMY_REQUESTED: ExpV2Action[] = [
  'start',
  'go_to_target',
  'raise',
  'resume',
  'resume_variant',
  'roll_out_winner',
];

export interface ActionOption {
  action: ExpV2Action;
  label: string;
  needs: 'none' | 'rolloutSize' | 'variant';
  danger: boolean;
  requested: boolean;
}

export const ACTION_LABEL: Record<ExpV2Action, string> = {
  start: 'Start',
  go_to_target: 'Go to target',
  raise: 'Raise %',
  pause: 'Pause',
  resume: 'Resume',
  pause_variant: 'Pause a variant',
  resume_variant: 'Resume a variant',
  remove_variant: 'Remove a variant',
  end: 'End',
  roll_out_winner: 'Roll out winner',
};

/** Label of an action name the server stored (a pending action is a plain string). */
export const actionLabel = (action: string): string =>
  ACTION_LABEL[action as ExpV2Action] ?? action;

const NEEDS: Partial<Record<ExpV2Action, ActionOption['needs']>> = {
  start: 'rolloutSize',
  raise: 'rolloutSize',
  pause_variant: 'variant',
  resume_variant: 'variant',
  remove_variant: 'variant',
  roll_out_winner: 'variant',
};

const DANGER: ExpV2Action[] = ['remove_variant', 'end', 'roll_out_winner'];

function actionsFor(t: ExpV2Test): ExpV2Action[] {
  const test = t.kind === 'experiment';
  switch (t.status) {
    case 'draft':
      return ['start'];
    case 'scheduled':
      return ['end'];
    case 'running':
      return test
        ? [
            ...(t.stablePhaseFrom ? [] : (['go_to_target'] as ExpV2Action[])),
            'raise',
            'pause',
            'pause_variant',
            'remove_variant',
            'end',
            'roll_out_winner',
          ]
        : ['raise', 'pause', 'end'];
    case 'paused':
      return test
        ? ['resume', 'pause_variant', 'resume_variant', 'remove_variant', 'end', 'roll_out_winner']
        : ['resume', 'end'];
    case 'ended':
      return test ? ['roll_out_winner'] : [];
    case 'rolled_out':
      return ['end'];
    default:
      return [];
  }
}

/**
 * [] without edit rights. While an economy request waits, only the actions that
 * apply at once stay (pause, pause/remove a variant, end): the server takes them,
 * and refuses a second request.
 */
export function availableActions(t: ExpV2Test, ctx: { canEdit: boolean }): ActionOption[] {
  if (!ctx.canEdit) return [];
  const options = actionsFor(t).map((action) => ({
    action,
    label: ACTION_LABEL[action],
    needs: NEEDS[action] ?? 'none',
    danger: DANGER.includes(action),
    requested: t.economy && ECONOMY_REQUESTED.includes(action),
  }));
  return t.pendingAction ? options.filter((o) => !o.requested) : options;
}

/** Pre-registration fields that keep Start disabled; flags need none. */
export function startBlockedBy(t: ExpV2Test, preMissing: string[]): string[] {
  return t.kind === 'experiment' ? preMissing : [];
}

export function variantChoices(t: ExpV2Test, action: ExpV2Action): string[] {
  const keys = (pick: (v: ExpV2Test['variants'][number]) => boolean) =>
    t.variants.filter(pick).map((v) => v.key);
  switch (action) {
    case 'pause_variant':
      return keys((v) => v.state === 'active' && v.key !== CONTROL);
    case 'resume_variant':
      return keys((v) => v.state === 'paused');
    case 'remove_variant':
      return keys((v) => v.state !== 'removed' && v.key !== CONTROL);
    case 'roll_out_winner':
      return keys((v) => v.state === 'active');
    default:
      return [];
  }
}

/** Null when `percent` is a valid new live size for Raise %. */
export function raiseProblem(t: ExpV2Test, percent: number): string | null {
  const buckets = percentToBuckets(percent);
  if (buckets === null) return 'Use 0% to 100% in steps of 0.1%';
  if (buckets <= t.rolloutSize) return `Must be above the current ${formatPercent(t.rolloutSize)}`;
  return null;
}

/**
 * Null when `percent` is a valid live size for Start or Raise %. Start stays inside
 * the reserved range; Raise % may go past it (the server grows the range into the
 * free buckets after it, or answers 409 if they are taken).
 */
export function rolloutSizeProblem(
  t: ExpV2Test,
  action: ExpV2Action,
  percent: number | null
): string | null {
  if (percent === null) return 'Enter a percent';
  if (action === 'raise') return raiseProblem(t, percent);
  const buckets = percentToBuckets(percent);
  if (buckets === null || buckets <= 0) return 'Use 0.1% to 100% in steps of 0.1%';
  if (buckets > t.rangeSize) return `At most the reserved ${formatPercent(t.rangeSize)}`;
  return null;
}

/** A waiting request's payload in the page's own words (percent, variant name). */
export function describePending(t: ExpV2Test, payload: Record<string, unknown>): string {
  if (typeof payload.rolloutSize === 'number') {
    return `To ${formatPercent(payload.rolloutSize)} of matching players (now ${formatPercent(t.rolloutSize)})`;
  }
  if (typeof payload.variant === 'string') return `Variant ${payload.variant}`;
  return '';
}

/** The requester never approves their own request; nobody does before identity loads. */
export function canApprove(t: ExpV2Test, adminId: string | undefined, canEdit: boolean): boolean {
  if (!t.pendingAction || !canEdit || !adminId) return false;
  return adminId !== t.pendingAction.requestedBy;
}

/**
 * True when the waiting request is still the one the admin was shown. Approve
 * and Reject re-read the test first, so a request replaced in the meantime
 * (rejected, then asked again with another size) is never approved blind.
 */
export function samePending(
  shown: ExpV2PendingAction | null | undefined,
  now: ExpV2PendingAction | null | undefined
): boolean {
  if (!shown || !now) return false;
  return (
    shown.action === now.action &&
    shown.requestedBy === now.requestedBy &&
    shown.requestedAt === now.requestedAt &&
    JSON.stringify(shown.payload) === JSON.stringify(now.payload)
  );
}

/** Any admin with edit rights may drop a waiting action, the requester included. */
export function canReject(t: ExpV2Test, canEdit: boolean): boolean {
  return Boolean(t.pendingAction) && canEdit;
}

export type ActionAnswer =
  | { status: number; data?: ExpV2ActionResult | { pending?: boolean } }
  | { error: unknown };

export function actionOutcome(
  answer: ActionAnswer,
  label: string,
  testKey: string
): { kind: 'success' | 'pending' | 'error'; text: string } {
  if ('error' in answer) {
    return { kind: 'error', text: apiErrorMessage(answer.error, `${label} failed`) };
  }
  if (answer.status === 202 || answer.data?.pending === true) {
    return {
      kind: 'pending',
      text: `${label} on ${testKey} is waiting for a second admin's approval`,
    };
  }
  return { kind: 'success', text: `${label} done on ${testKey}` };
}

/** QA overrides added in one paste (the server's limit). */
export const OVERRIDE_MAX = 200;

/**
 * Ids pasted one per line or separated by spaces or commas, repeats dropped.
 * Player ids are lower-cased (as the server does); device ids keep their case,
 * because the server matches them exactly.
 */
export function parseIds(
  text: string,
  max: number,
  opts: { lowercase?: boolean } = {}
): { ids: string[]; problem?: string } {
  const lower = opts.lowercase ?? true;
  const ids = [
    ...new Set(
      text
        .split(/[\s,]+/)
        .map((s) => (lower ? s.trim().toLowerCase() : s.trim()))
        .filter((s) => s !== '')
    ),
  ];
  if (ids.length === 0) return { ids, problem: 'Paste at least one id' };
  if (ids.length > max) return { ids, problem: `${ids.length} ids; ${max} at most` };
  return { ids };
}

/** Player ids on one user list (the server's limit). */
export const LIST_MAX = 10_000;
const PLAYER_ID = /^[0-9a-f-]{36}$/;

/** A user list holds player ids only; the first few strays are named. */
export function parseUserIds(text: string): { ids: string[]; problem?: string } {
  const parsed = parseIds(text, LIST_MAX);
  if (parsed.problem) return parsed;
  const bad = parsed.ids.filter((id) => !PLAYER_ID.test(id));
  if (bad.length === 0) return parsed;
  const more = bad.length > 5 ? ` and ${bad.length - 5} more` : '';
  return { ids: parsed.ids, problem: `Not player ids: ${bad.slice(0, 5).join(', ')}${more}` };
}
